"use client";

// Positions the live organism's CENTER at one of 9 anchor points (the 3×3 grid)
// — a consistent location system used across pages. `offset` (CSS lengths) lets
// a corner anchor be nudged to land exactly on a UI element (e.g. the avatar),
// and because anchors are viewport fractions the placement holds at any width.
//
// LAYERS, decoupled on purpose:
//   • a FIELD the size of the viewport (or the parent, when not fixed) that is a
//     size container, so `cqw`/`cqh` mean exactly what `left: 50%` used to mean;
//   • X then Y — the anchor + offset as `translateX`/`translateY` on two nested
//     layers, each with its own glide, so a single-axis slide animates one axis
//     only. law_snappy: a relocation is compositor-only. It used to animate
//     `left`/`top`, which re-ran layout on every frame of a 1.15s glide.
//   • INNER carries the scale + opacity (the dive-grow and the collapse/regrow
//     morph). Keeping scale off the positioners means an anchor SNAP never drags
//     a diagonal slide under a dissolve.

import "./organism-focus.css";
import type { CSSProperties } from "react";
import MemoryOrganism, { type OrganismFocus } from "./MemoryOrganism";
import type { ColorState, RGB } from "../lib/organism/color";
import type { MacroState } from "../lib/organism/macro-tuners";

export type Anchor =
  | "top-left" | "top" | "top-right"
  | "left" | "center" | "right"
  | "bottom-left" | "bottom" | "bottom-right";

// the anchor as a fraction of the field (x, y)
const ANCHOR_POS: Record<Anchor, [number, number]> = {
  "top-left": [0, 0], top: [0.5, 0], "top-right": [1, 0],
  left: [0, 0.5], center: [0.5, 0.5], right: [1, 0.5],
  "bottom-left": [0, 1], bottom: [0.5, 1], "bottom-right": [1, 1],
};

const GLIDE = "1.15s var(--ease-drift)";

type Props = {
  anchor?: Anchor;
  size?: number | string;
  /** CSS-length nudge of the center, e.g. { x: "calc(...)", y: "1.6rem" }. */
  offset?: { x?: string; y?: string };
  blur?: number;
  opacity?: number;
  /** Visual scale at the anchor (transform on the inner layer). */
  scale?: number;
  /** Radial edge fade (0..1 = where the fade begins; 0 disables). */
  fade?: number;
  /** Animate anchor/offset changes (the anchor-glide). */
  glide?: boolean;
  /** Which axis the glide animates — a single-axis slide vs both. */
  glideAxis?: "x" | "y" | "both" | "none";
  /** Transition (ms) for the inner scale+opacity — drives the dive grow and the
   *  collapse/regrow morph. */
  innerTransMs?: number;
  /** Easing for the inner scale+opacity transition — e.g. a strong ease-in on
   *  the regrow so the outer-petal spin builds as it opens. */
  innerEase?: string;
  fixed?: boolean;
  zIndex?: number;
  /** THE FOCUS PULL (law_read_frost → law_focus_pull, 2026-09-29): while the
   *  page this netti stands behind is being read, or the place is deep enough
   *  that netti rests in the background (lib/os/nettiFocus), the creature goes
   *  out of focus: ONE uniform `filter: blur()` on its own layer, never a frost
   *  laid over it, so there is no sharp copy showing through a blurred one and
   *  no mask edge. Opt in on every surface people READ (the dashboard, admin,
   *  docs, shares, rooms); leave it off where netti is the hero (landing, deck,
   *  onboarding, auth) and on the full-bloom surfaces (law_full_bloom). */
  readingFrost?: boolean;
  // organism drive-through
  focus?: OrganismFocus | null;
  rotation?: number;
  spin?: boolean;
  /** Slow curated hue arc (signed-out radiance). Flows to MemoryOrganism. */
  wander?: boolean;
  /** Showpiece mood-journey (the waitlist landing). Flows to MemoryOrganism. */
  showcase?: boolean;
  pulse?: number;
  /** Live mode — the organism wires its own nervous system (fires on memory
   *  changes via the in-app bus + Supabase realtime). Default ON; pass false on
   *  purely decorative surfaces that should not subscribe. Flows to MemoryOrganism. */
  live?: boolean;
  age?: number;
  /** Subscribe to the imperative age bus (window `innernet:organism-age`) so a
   *  timeline can scrub the bloom's age at 60fps without re-rendering React. */
  ageBus?: boolean;
  /** Subscribe to the imperative drive bus (window `innernet:organism-drive`) so
   *  a controller (e.g. the GroundDial) can repaint the bloom's ground/colour at
   *  60fps without re-rendering React. */
  driveBus?: boolean;
  grow?: boolean;
  renderScale?: number;
  /** Cap the device-pixel multiplier — the other half of the fill-rate cost.
   *  A surface that runs all day (the desktop wallpaper) pins this low. */
  maxDpr?: number;
  macros?: Partial<MacroState>;
  /** Live tuner patch for the future engine (venation · nodes · innerLight …) —
   *  flows to MemoryOrganism, which dresses the ONE organism rather than minting
   *  a second. */
  tune?: Record<string, number>;
  /** The person's own tuned body (live mint + hand override) — identity, not
   *  dressing. Flows to MemoryOrganism. */
  body?: Record<string, number>;
  color?: ColorState;
  bg?: RGB;
  publishHue?: boolean;
  /** WARM COIL variant — flows to MemoryOrganism. At warm=false the flower renders
   *  unchanged. */
  warm?: boolean;
  /** ASCII veil overlay — flows to MemoryOrganism. Off by default. */
  veil?: boolean;
  /** Per-mount veil shader overrides — flows to MemoryOrganism (merged over the
   *  asciiConfig defaults). Used to seed netti's saved dither look. */
  veilTune?: Record<string, number>;
  /** Form seed — distinct per user so the petal arrangement is unique too. */
  seed?: number;
  /** Which organism generation renders — "future" (netti next-gen) or "coil".
   *  Flows to MemoryOrganism (which defaults to "future"). */
  engine?: "future" | "coil";
};

export default function OrganismBackdrop({
  anchor = "center",
  size = "min(900px, 92vw)",
  offset,
  // PERF: no CSS blur by default. `filter: blur()` on the live canvas re-runs a
  // gaussian over the whole ~1120px layer EVERY frame it repaints (and worse,
  // re-rasterizes it while the choreography transforms it) — a continuous
  // compositor cost for a canvas that's already a soft watercolour. The engine's
  // own render rim carries edge softness; callers only pass blur>0 deliberately.
  blur = 0,
  opacity = 0.6,
  scale = 1,
  fade = 0.58,
  glide = true,
  glideAxis = "both",
  innerTransMs = 900,
  innerEase = "var(--ease-soft)",
  fixed = true,
  zIndex = 0,
  readingFrost = false,
  ...org
}: Props) {
  const [x, y] = ANCHOR_POS[anchor];
  const ox = offset?.x ?? "0px";
  const oy = offset?.y ?? "0px";
  // Compose `-50% ± offset` safely: a negative offset must read as `calc(-50% -
  // 14vh)`, never `calc(-50% + -14vh)` (a double sign is invalid CSS and the
  // whole transform is dropped — which silently zeroed the nudge).
  const axis = (base: string, off: string) => {
    const o = (off ?? "").trim();
    if (!o || o === "0px" || o === "0") return base;
    const neg = o.startsWith("-");
    return `calc(${base} ${neg ? "-" : "+"} ${neg ? o.slice(1) : o})`;
  };

  // NO MASK (Task 3). The organism sits UNMASKED — full bleed. Its opaque GL
  // canvas is painted with the page ground (uBg), so the empty canvas area is
  // invisible against the page; the bloom's own soft watercolour rim + the
  // backdrop back-haze fade at the bloom radius, melting into the ground with no
  // vignette ring. We deliberately do NOT apply a CSS radial mask — re-adding one
  // would re-introduce the visible masked-disc look the founder wants gone. The
  // `fade` prop is kept on the API (callers still pass it) but no longer drives a
  // CSS mask; edge softness lives in the render rim (organismGL) instead.
  const sizeStr = typeof size === "number" ? `${size}px` : size;

  // each positioner glides its own axis
  const glideX = glide && glideAxis !== "y" && glideAxis !== "none" ? `transform ${GLIDE}` : undefined;
  const glideY = glide && glideAxis !== "x" && glideAxis !== "none" ? `transform ${GLIDE}` : undefined;

  return (
    <div
      aria-hidden
      style={{
        position: fixed ? "fixed" : "absolute",
        inset: 0,
        // the field is measured, never measures: a size container whose size
        // comes from its inset, so cqw/cqh are the viewport (or parent) exactly.
        containerType: "size",
        pointerEvents: "none",
        zIndex,
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: sizeStr,
          height: sizeStr,
          transform: `translateX(${axis(`calc(${x} * 100cqw - 50%)`, ox)})`,
          transition: glideX,
        }}
      >
      <div
        style={{
          width: "100%",
          height: "100%",
          transform: `translateY(${axis(`calc(${y} * 100cqh - 50%)`, oy)})`,
          transition: glideY,
        }}
      >
      <div
        className={readingFrost ? "ob-focus" : undefined}
        style={{
          width: "100%",
          height: "100%",
          // THE READING POSE (organism-focus.css) seats netti's heart at one
          // place in the room whatever this surface's anchor is: it needs to
          // know where that anchor puts it. A netti seated at the top (the you
          // space's avatar) keeps its seat.
          ...(readingFrost
            ? ({
                "--ob-ax": String(x),
                "--ob-ay": String(y),
                "--ob-ox": ox,
                "--ob-oy": oy,
                "--ob-pose": y >= 0.5 ? "1" : "0",
              } as CSSProperties)
            : null),
          transform: `scale(${scale})`,
          transformOrigin: "center",
          opacity,
          // Only apply a CSS blur when a caller deliberately asks for one (>0);
          // otherwise omit the filter entirely so there's no per-frame gaussian
          // re-raster of the live canvas (see the `blur` default note above).
          ...(blur > 0.05 ? { filter: `blur(${blur}px)` } : null),
          // NEVER FADE (Task 7, Law 4): only SCALE is transitioned on the inner
          // layer — the collapse/regrow de-ages by shrinking to the nucleus and
          // growing back, and the organism stays fully present. Opacity is applied
          // but NOT animated, so navigating between sections can never cross-fade
          // the organism in or out during a relocation.
          // …and the focus pull, when this netti takes one: a short ease between
          // the quantised radii the scroll writes (lib/os/nettiFocus), and the
          // reading pose's slow settle (organism-focus.css: `scale` and
          // `translate` compose with the transform above, never replace it).
          transition: readingFrost
            ? `transform ${innerTransMs}ms ${innerEase}, filter var(--netti-focus-dur) var(--ease-soft), scale var(--netti-read-dur) var(--ease-read), translate var(--netti-read-dur) var(--ease-read)`
            : `transform ${innerTransMs}ms ${innerEase}`,
          // No mask-image (Task 3): full bleed. See note above.
        }}
      >
        <MemoryOrganism {...org} lens={readingFrost} />
      </div>
      </div>
      </div>
    </div>
  );
}
